import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { clearErrors, getOrderDetails } from '../../actions/orderAction';
import Loader from '../Layouts/Loader';
import MetaData from '../Layouts/MetaData';
import DownloadIcon from '@mui/icons-material/Download';
import CircleIcon from '@mui/icons-material/Circle';
import { formatDate } from '../../utils/functions';
import LiveTracking from './LiveTracking';
import { Link } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import Tooltip from '@mui/material/Tooltip';
import { useLang, uiText, translateStatus } from '../../utils/i18n';

const LOCKED_STATUSES = ['Packed', 'Shipped', 'Out for Delivery', 'Delivered', 'Cancelled'];
const INVOICE_AVAILABLE_STATUSES = ['Packed', 'Shipped', 'Out for Delivery', 'Delivered'];

const statusColors = {
    Processing: "text-primary-green",
    Accepted: "text-blue-600",
    Packed: "text-blue-600",
    Shipped: "text-primary-orange",
    Delivered: "text-primary-green",
    Cancelled: "text-red-500",
};

const OrderDetails = () => {

    const dispatch = useDispatch();
    const { lang } = useLang();
    const [pointsRate, setPointsRate] = useState(100); // fallback matches the default redemption rate

    useEffect(() => {
        axios.get('/api/v1/wallet-settings').then(({ data }) => setPointsRate(data.settings.pointsToCurrencyRedemption)).catch(() => {});
    }, []);
    const { enqueueSnackbar } = useSnackbar();
    const params = useParams();

    const { order, error, loading } = useSelector((state) => state.orderDetails);

    useEffect(() => {
        if (error) {
            enqueueSnackbar(error, { variant: "error" });
            dispatch(clearErrors());
        }
        dispatch(getOrderDetails(params.id));
    }, [dispatch, error, params.id, enqueueSnackbar]);

    const downloadInvoiceHandler = async () => {
        try {
            const { data } = await axios.get(`/api/v1/order/${order._id}/invoice`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `invoice-${order.orderNumber ?? order._id}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            enqueueSnackbar(uiText('failedDownloadInvoice', lang), { variant: "error" });
        }
    };

    const itemsSubtotal = order?.orderItems?.reduce((sum, item) => sum + (item.price * item.quantity), 0) || 0;
    const statusColor = order ? (statusColors[order.orderStatus] || statusColors.Processing) : null;

    const statusLabel = (o) => {
        if (o.orderStatus === 'Processing') return `${uiText('orderedOn', lang)} ${formatDate(o.createdAt)}${uiText('orderedOnSuffix', lang)}`;
        if (o.orderStatus === 'Shipped') return `${translateStatus('Shipped', lang)}${lang === 'zh' ? '於' : ' on'} ${formatDate(o.shippedAt)}`;
        if (o.orderStatus === 'Delivered') return `${uiText('deliveredOn', lang)} ${formatDate(o.deliveredAt)}${uiText('deliveredOnSuffix', lang)}`;
        return translateStatus(o.orderStatus === 'Accepted' ? 'Order Accepted' : o.orderStatus === 'Packed' ? 'Order Packed' : o.orderStatus, lang);
    };

    return (
        <>
            <MetaData title="Order Details | 1313 Mart" />

            <main className="w-full mt-14 sm:mt-4">
                {loading ? <Loader /> : (
                    <>
                        {order && order.user && order.shippingInfo && (
                            <div className="flex flex-col gap-4 max-w-6xl mx-auto">

                                <div className="flex flex-col sm:flex-row bg-white shadow rounded-sm min-w-full">
                                    <div className="sm:w-1/2 border-r">
                                        <div className="flex flex-col gap-3 my-8 mx-10">
                                            <h3 className="font-medium text-lg">{uiText('deliveryAddressLabel', lang)}</h3>
                                            <h4 className="font-medium">{order.user.name}</h4>
                                            <p className="text-sm">{`${order.shippingInfo.address}, ${order.shippingInfo.city}, ${order.shippingInfo.state}`}</p>
                                            <div className="flex gap-2 text-sm">
                                                <p className="font-medium">{uiText('emailLabelPlain', lang)}</p>
                                                <p>{order.user.email}</p>
                                            </div>
                                            <div className="flex gap-2 text-sm">
                                                <p className="font-medium">{uiText('phoneNumberLabel', lang)}</p>
                                                <p>{order.shippingInfo.phoneNo}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="sm:w-1/2 flex flex-col justify-center gap-3 my-8 mx-10">
                                        <h3 className="font-medium text-lg">{uiText('orderStatusHeading', lang)}</h3>
                                        <p className="text-sm text-gray-500">{uiText('orderNumberHash', lang)}{order.orderNumber ?? order._id}</p>
                                        <p className={`text-sm font-medium flex items-center gap-1.5 ${statusColor}`}>
                                            <CircleIcon sx={{ fontSize: "12px" }} /> {statusLabel(order)}
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            {INVOICE_AVAILABLE_STATUSES.includes(order.orderStatus) && (
                                                <button
                                                    onClick={downloadInvoiceHandler}
                                                    className="flex items-center justify-center gap-1.5 bg-primary-blue text-white text-sm font-medium px-4 py-2 rounded shadow hover:shadow-lg w-fit"
                                                >
                                                    <DownloadIcon sx={{ fontSize: "18px" }} /> {uiText('downloadInvoice', lang)}
                                                </button>
                                            )}

                                            {LOCKED_STATUSES.includes(order.orderStatus) ? (
                                                <Tooltip title={uiText('orderLockedTooltip', lang)}>
                                                    <span>
                                                        <button disabled className="flex items-center justify-center gap-1.5 bg-gray-200 text-gray-400 text-sm font-medium px-4 py-2 rounded w-fit cursor-not-allowed">
                                                            <EditIcon sx={{ fontSize: "18px" }} /> {uiText('editOrder', lang)}
                                                        </button>
                                                    </span>
                                                </Tooltip>
                                            ) : (
                                                <Link
                                                    to={`/order_details/${order._id}/edit`}
                                                    className="flex items-center justify-center gap-1.5 bg-primary-orange text-white text-sm font-medium px-4 py-2 rounded shadow hover:shadow-lg w-fit"
                                                >
                                                    <EditIcon sx={{ fontSize: "18px" }} /> {uiText('editOrder', lang)}
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <LiveTracking order={order} />

                                <div className="bg-white shadow rounded-sm overflow-hidden">
                                    <table className="w-full text-sm">
                                        <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
                                            <tr>
                                                <th className="text-left px-4 py-3">{uiText('tableItem', lang)}</th>
                                                <th className="text-right px-4 py-3">{uiText('tableQty', lang)}</th>
                                                <th className="text-right px-4 py-3">{uiText('tablePrice', lang)}</th>
                                                <th className="text-right px-4 py-3">{uiText('tableTotal', lang)}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {order.orderItems.map((item) => (
                                                <tr key={item._id} className="border-t">
                                                    <td className="px-4 py-3">
                                                        <div className="flex items-center gap-3">
                                                            <img draggable="false" className="w-12 h-12 object-contain flex-shrink-0" src={item.image} alt={item.name} />
                                                            <span>{item.name.length > 60 ? `${item.name.substring(0, 60)}...` : item.name}</span>
                                                        </div>
                                                    </td>
                                                    <td className="text-right px-4 py-3">{item.quantity}</td>
                                                    <td className="text-right px-4 py-3">HK${item.price.toLocaleString()}</td>
                                                    <td className="text-right px-4 py-3 font-medium">HK${(item.price * item.quantity).toLocaleString()}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>

                                    <div className="border-t bg-gray-50 p-4 flex flex-col gap-1 items-end text-sm">
                                        <p className="flex justify-between w-full sm:w-64"><span>{uiText('mrpSubtotal', lang)}</span> <span>HK${(order.subtotal ?? itemsSubtotal).toLocaleString()}</span></p>
                                        {order.offerDiscount > 0 && (
                                            <p className="flex justify-between w-full sm:w-64 text-primary-green"><span>{uiText('offerDiscountLabel', lang)}</span> <span>- HK${order.offerDiscount.toLocaleString()}</span></p>
                                        )}
                                        {order.promoDiscount > 0 && (
                                            <p className="flex justify-between w-full sm:w-64 text-primary-green"><span>{uiText('promoLabel', lang)} ({order.promoCode})</span> <span>- HK${order.promoDiscount.toLocaleString()}</span></p>
                                        )}
                                        <p className="flex justify-between w-full sm:w-64"><span>{uiText('shippingChargeLabel', lang)}</span> <span>{order.shippingCharge > 0 ? `HK$${order.shippingCharge.toLocaleString()}` : uiText('freeLabel', lang)}</span></p>
                                        {order.pointsRedeemed > 0 && (
                                            <p className="flex justify-between w-full sm:w-64 text-primary-orange"><span>{uiText('pointsUsedLabel', lang)} ({order.pointsRedeemed.toLocaleString()} {uiText('ptsWord', lang)})</span> <span>- HK${(order.pointsRedeemed / pointsRate).toLocaleString()}</span></p>
                                        )}
                                        {order.walletCashUsed > 0 && (
                                            <p className="flex justify-between w-full sm:w-64 text-primary-green"><span>{uiText('cashUsedLabel', lang)}</span> <span>- HK${order.walletCashUsed.toLocaleString()}</span></p>
                                        )}
                                        <p className="flex justify-between w-full sm:w-64 font-medium text-base border-t pt-1 mt-1"><span>{uiText('totalPaidLabel', lang)}</span> <span>HK${order.totalPrice.toLocaleString()}</span></p>
                                        {order.pointsEarned > 0 && (
                                            <p className="flex justify-between w-full sm:w-64 text-xs text-gray-400 mt-1"><span>{uiText('youEarnedEmoji', lang)}</span> <span>{order.pointsEarned.toLocaleString()} {uiText('pointsWord', lang)}</span></p>
                                        )}
                                    </div>
                                </div>

                                {order.logs && order.logs.length > 0 && (
                                    <div className="bg-white shadow rounded-sm p-6 flex flex-col gap-3">
                                        <h3 className="font-medium text-lg">{uiText('orderUpdatesHeading', lang)}</h3>
                                        <div className="flex flex-col gap-3 max-h-64 overflow-y-auto pr-1">
                                            {[...order.logs].reverse().map((log, i) => (
                                                <div key={i} className="border-l-2 border-primary-blue pl-3 py-0.5">
                                                    <p className="text-sm text-gray-800">{log.message}</p>
                                                    <p className="text-xs text-gray-400">{new Date(log.createdAt).toLocaleString('en-HK')}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                            </div>
                        )}
                    </>
                )}
            </main>
        </>
    );
};

export default OrderDetails;